import { Button, Icon, Typography } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import { FeeBreakdownInfoModal } from '@hero/components/modals';
import { useCreatePriceLabel } from '@hero/hooks';
import { useCollectionsCreationHeader } from '@hero/hooks/collections/useCollectionsCreationHeader';
import { PageLayout } from '@hero/layouts';
import { ProductSelectionSection, ShoppingCart } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetCollection, useUpdateCollection } from '@hero/services';
import { formatCurrency, getFormattedPrice } from '@hero/utils';

export default function CollectionsNewEditPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { collectionId } = useParams();

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isFeeBreakdownModalOpen, setIsFeeBreakdownModalOpen] = useState(false);

  const { data: collection } = useGetCollection({ collectionId });
  const { mutateAsync: updateCollection } = useUpdateCollection();

  const handleCancel = () => navigate(ROUTE_PATHS.COLLECTIONS());

  const handleConfirm = async () => {
    await updateCollection({
      params: { collectionId },
      body: {
        products: selectedProducts.map(product => ({
          id: product.merchantProductId,
          merchantProductId: product.merchantProductId,
          source: product?.source || undefined,
          images: product?.images.map(i => ({ id: i.id, url: i.url })),
          name: product?.name,
          brand: product?.brand,
          skus: product.skus.map(sku => ({
            code: sku.code,
            price: sku.price
          }))
        }))
      }
    });
    navigate(ROUTE_PATHS.COLLECTIONS_DETAIL(collectionId));
  };

  const header = useCollectionsCreationHeader({ name: collection?.name, setIsPageLoading });

  const { isCAActMustBeApplied } = useCreatePriceLabel({});

  return (
    <PageLayout
      header={header}
      navigation={{
        rightButton: {
          children: t('createCollection'),
          onClick: handleConfirm,
          disabled: !selectedProducts?.length,
          icon: true
        },
        leftButton: {
          children: t('back'),
          onClick: handleCancel
        }
      }}
    >
      <ProductSelectionSection
        selectedProducts={selectedProducts}
        onChangeSelectedProductList={selectedProducts => {
          setSelectedProducts(selectedProducts);
        }}
        showSaveAsCollectionButton={false}
        localShoppingCartEnabled
        featureType="collection"
        components={{
          WidgetFooter: props => (
            <>
              <ShoppingCart.S.WidgetFooterSubtotal>
                <Typography variant="body4">
                  {t('priceRange')}:
                  <Typography variant="body3Bold" as="span">
                    {selectedProducts?.length > 0
                      ? getFormattedPrice({ min: props?.minPrice, max: props?.maxPrice })
                      : formatCurrency(0)}
                  </Typography>
                </Typography>
                {isCAActMustBeApplied && (
                  <ShoppingCart.S.WidgetFooterFeeBreakdownModalButton
                    variant="link"
                    onClick={() => setIsFeeBreakdownModalOpen(true)}
                  >
                    {t('feeBreakdownModalOpenButtonText')}
                  </ShoppingCart.S.WidgetFooterFeeBreakdownModalButton>
                )}
              </ShoppingCart.S.WidgetFooterSubtotal>
              <ShoppingCart.S.FooterActionButtons>
                <Button endIcon={<Icon name="next" />} onClick={handleConfirm} block>
                  {t('createCollection')}
                </Button>
              </ShoppingCart.S.FooterActionButtons>
              {isFeeBreakdownModalOpen && (
                <FeeBreakdownInfoModal
                  isOpen={isFeeBreakdownModalOpen}
                  onRequestClose={() => setIsFeeBreakdownModalOpen(false)}
                />
              )}
            </>
          )
        }}
      />
      {isPageLoading && <AppLoader />}
    </PageLayout>
  );
}
